// The one API client (frontend-components, API Integration). It sends the
// cookie, the custom header that proves a request came from Caravan's own
// pages (NFR4.3), and the Idempotency-Key, and maps every error to its code.
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly fields: { field: string; code: string }[] = [],
  ) {
    super(code);
    this.name = 'ApiError';
  }
}

export interface ApiClient {
  get<T = unknown>(path: string): Promise<T>;
  post<T = unknown>(
    path: string,
    body?: unknown,
    options?: { idempotencyKey?: string },
  ): Promise<T>;
  // U2: PATCH /me.
  patch<T = unknown>(path: string, body: unknown): Promise<T>;
  // U3: the application and the profile are multipart, carrying the photo.
  // U5: creating (POST, with its Idempotency-Key) and changing an offering.
  sendForm<T = unknown>(
    method: 'PUT' | 'PATCH' | 'POST',
    path: string,
    form: FormData,
    options?: { idempotencyKey?: string },
  ): Promise<T>;
  // U4: DELETE /coach/zoom-connection.
  delete<T = unknown>(path: string): Promise<T>;
  // U7: a GET whose answer is text, not JSON (the calendar file).
  getText(path: string): Promise<string>;
  // Calls back when the API says Caravan is paused (budget stop).
  onPaused(listener: () => void): () => void;
}

interface ErrorBody {
  code?: unknown;
  fields?: unknown;
}

export function createApiClient(options: { apiBase: string; fetchImpl?: typeof fetch }): ApiClient {
  const fetchImpl = options.fetchImpl ?? ((input, init) => fetch(input, init));
  const pausedListeners = new Set<() => void>();

  async function request<T>(
    method: string,
    path: string,
    body?: unknown,
    idempotencyKey?: string,
    isText = false,
  ): Promise<T> {
    // U3: a form goes as it is, so the browser sets the multipart boundary.
    const isForm = body instanceof FormData;
    const headers: Record<string, string> = isForm ? {} : { 'Content-Type': 'application/json' };
    if (method !== 'GET') headers['X-Caravan-Request'] = '1';
    if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;
    let response: Response;
    try {
      response = await fetchImpl(`${options.apiBase}${path}`, {
        method,
        credentials: 'include',
        headers,
        ...(body === undefined ? {} : { body: isForm ? body : JSON.stringify(body) }),
      });
    } catch {
      // No answer at all (offline, a dropped connection): status 0 with the
      // client's existing INTERNAL_ERROR code. Callers decide what to show;
      // screens that load through useLoad (load-state.tsx) show their error
      // with Try again.
      throw new ApiError(0, 'INTERNAL_ERROR');
    }
    if (response.status === 204) return undefined as T;
    if (isText && response.ok) return (await response.text()) as T;
    const parsed: unknown = await response.json().catch(() => undefined);
    if (response.ok) return parsed as T;
    const error = (parsed ?? {}) as ErrorBody;
    // API Gateway's own 429 has no Caravan body.
    const code =
      typeof error.code === 'string'
        ? error.code
        : response.status === 429
          ? 'TOO_MANY_REQUESTS'
          : 'INTERNAL_ERROR';
    if (code === 'SERVICE_PAUSED') pausedListeners.forEach((listener) => listener());
    const fields = Array.isArray(error.fields)
      ? (error.fields as { field: string; code: string }[])
      : [];
    throw new ApiError(response.status, code, fields);
  }

  return {
    get: (path) => request('GET', path),
    post: (path, body, requestOptions) =>
      request('POST', path, body, requestOptions?.idempotencyKey),
    patch: (path, body) => request('PATCH', path, body),
    sendForm: (method, path, form, requestOptions) =>
      request(method, path, form, requestOptions?.idempotencyKey),
    delete: (path) => request('DELETE', path),
    getText: (path) => request<string>('GET', path, undefined, undefined, true),
    onPaused: (listener) => {
      pausedListeners.add(listener);
      return () => pausedListeners.delete(listener);
    },
  };
}
